<template>
    <div class="add-prompts-root">
        <!-- Header -->
        <div class="header-slab">
            <div class="d-flex align-center justify-space-between">
                <div>
                    <h2 class="page-title">Add Prompts</h2>
                    <p class="page-subtitle mb-0">Select or generate prompts you want to monitor in this project.</p>
                </div>
                <v-chip outlined color="primary" class="font-weight-medium"> {{ allPrompts.length }} selected </v-chip>
            </div>

            <!-- Credits / Kosten-Übersicht -->
            <div class="credits-panel mt-3">
                <div class="credits-panel__item">
                    <v-progress-circular v-if="creditsLoading" indeterminate size="16" width="2" color="primary" class="mr-2" />
                    <v-icon v-else small class="mr-2" color="primary">mdi-wallet-outline</v-icon>
                    <span class="credits-panel__label">Available credits</span>
                    <span class="credits-panel__value">{{ formatNumber(promptCredits.remaining) }}</span>
                </div>

                <v-divider vertical class="mx-3 credits-panel__divider" />

                <div class="credits-panel__item">
                    <v-icon small class="mr-2" :color="creditsExceeded ? 'error' : 'primary'">mdi-calculator-variant-outline</v-icon>
                    <span class="credits-panel__label">Estimated cost</span>
                    <span class="credits-panel__value" :class="{ 'error--text': creditsExceeded }">{{ formatNumber(estimatedCreditCost) }}</span>
                    <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" x-small class="ml-1" color="grey">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>
                            {{ allPrompts.length }} prompt(s) × {{ intervalRunsPerMonth }} run(s)/month ({{ intervalLabel }}) × {{ platformCount }} platform(s) =
                            {{ formatNumber(estimatedCreditCost) }} credits per month.
                        </span>
                    </v-tooltip>
                </div>

                <template v-if="creditsLoaded">
                    <v-divider vertical class="mx-3 credits-panel__divider" />
                    <div class="credits-panel__item">
                        <v-icon small class="mr-2" :color="creditsExceeded ? 'error' : 'success'">
                            {{ creditsExceeded ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}
                        </v-icon>
                        <span class="credits-panel__label">Remaining after</span>
                        <span class="credits-panel__value" :class="creditsExceeded ? 'error--text' : 'success--text'">{{ formatNumber(creditsRemainingAfter) }}</span>
                    </div>
                </template>
            </div>

            <v-alert v-if="creditsExceeded" type="error" dense text class="mt-2 mb-0 credits-panel__alert">
                The selected prompts would require {{ formatNumber(estimatedCreditCost) }} credits, but only {{ formatNumber(promptCredits.remaining) }} are available.
            </v-alert>
        </div>

        <!-- Tabs -->
        <v-tabs v-model="tab" background-color="white" class="elevated-tabbar" color="primary" grow>
            <v-tab>
                <v-icon left small>mdi-clipboard-text</v-icon>
                Paste
            </v-tab>
            <!-- NEU: Upload Tab -->
            <v-tab>
                <v-icon left small>mdi-file-delimited</v-icon>
                Upload
            </v-tab>
            <v-tab>
                <v-icon left small color="blue">mdi-star-four-points</v-icon>
                Generate
            </v-tab>
            <v-tab>
                <v-icon left small class="adv-gen-icon">mdi-shape-plus</v-icon>
                Advanced Generation
            </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="tab-panels">
            <!-- Paste Tab -->
            <v-tab-item>
                <v-card flat class="panel-card">
                    <div class="section-header d-flex align-center">
                        <v-spacer />
                        <v-btn small text color="primary" @click="clearInput" :disabled="!kw_textfield"> <v-icon small class="mr-1">mdi-backspace</v-icon>Clear </v-btn>
                        <v-btn small text color="primary" @click="addFromClipboard"> <v-icon small class="mr-1">mdi-clipboard-arrow-down</v-icon>From Clipboard </v-btn>
                        <v-divider vertical class="mx-3" />
                        <v-btn small color="primary" @click="copyPromptsToClipboard" :disabled="!allPrompts.length"> <v-icon small left>mdi-content-copy</v-icon>Copy All </v-btn>
                    </div>
                    <v-textarea
                        v-model="kw_textfield"
                        class="mt-4 mono-input"
                        label="Paste prompts here..."
                        hint="One prompt per line – duplicates are auto-removed"
                        auto-grow
                        filled
                        rounded
                        dense
                        @input="textChanged" />
                    <div v-if="pasteOverLongLines.length" class="paste-toolong-hint error--text mt-1">
                        <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                        {{ pasteOverLongLines.length }} line(s) exceed {{ maxPromptLength }} characters and will be rejected:
                        {{ pasteOverLongLines.map((l) => `#${l.idx} (${l.length}/${maxPromptLength})`).join(", ") }}
                    </div>
                </v-card>
            </v-tab-item>
            <!-- NEU: Upload Tab Panel -->
            <v-tab-item>
                <v-card flat class="panel-card">
                    <v-card-title>
                        <v-icon left small class="mr-2">mdi-file-delimited</v-icon>
                        Upload prompts via CSV
                        <v-spacer />
                        <v-btn small text @click="downloadTemplate"> <v-icon left small>mdi-download</v-icon> CSV template </v-btn>
                    </v-card-title>
                    <v-card-subtitle>CSV delimiter: semicolon (;). Tags separated by comma (,).</v-card-subtitle>
                    <v-divider />

                    <v-card-text>
                        <v-file-input v-model="csvFile" accept=".csv,text/csv" rounded filled label="Choose CSV file" @change="handleCSVFile" />

                        <v-expand-transition>
                            <div v-if="csvHeaders.length" class="mt-4">
                                <div class="overline mb-2">Column mapping</div>
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <v-select rounded filled :items="csvHeaders" v-model="csvMapping.keyword" label="Prompt column" dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select rounded filled :items="csvHeaders" v-model="csvMapping.tags" label="Tags column (optional)" dense hide-details />
                                    </v-col>
                                </v-row>

                                <!-- <v-row dense>
                                   <v-col cols="12" sm="6">
                                        <v-select :items="tagSeparators" v-model="tagSeparator" item-text="label" item-value="value" label="Tag separator" outlined dense hide-details />
                                    </v-col> 
                                    <v-col cols="12" sm="6">
                                        <v-switch v-model="dedupeTags" label="Deduplicate tags" inset dense />
                                    </v-col> 
                                </v-row> -->
                            </div>
                        </v-expand-transition>

                        <v-alert v-if="csvError" type="error" dense outlined class="mt-3">{{ csvError }}</v-alert>
                        <v-alert v-if="csvWarning" type="warning" dense outlined class="mt-3">{{ csvWarning }}</v-alert>

                        <v-expand-transition>
                            <div v-if="csvRows.length" class="mt-4">
                                <div class="overline mb-2">Preview (first 10)</div>
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Prompt</th>
                                            <th>Tags</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, i) in previewRows" :key="i">
                                            <td>{{ i + 1 }}</td>
                                            <td :class="{ 'error--text': row.tooLong }">
                                                {{ row.keyword }}
                                                <div v-if="row.tooLong" class="csv-toolong-hint error--text">
                                                    {{ row.keyword.length }}/{{ maxPromptLength }} characters – too long, will be skipped
                                                </div>
                                            </td>
                                            <td>
                                                <v-chip v-for="t in row.tags" :key="t" small class="ma-1" outlined>{{ t }}</v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>

                                <div class="mt-4 d-flex align-center">
                                    <v-spacer />
                                    <v-btn color="primary" rounded :disabled="!canApplyCSV" @click="applyCSV">
                                        <v-icon left small>mdi-check</v-icon>
                                        Use {{ csvAppliedCount }} prompts
                                    </v-btn>
                                </div>
                            </div>
                        </v-expand-transition>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <!-- Generate Tab -->
            <v-tab-item>
                <v-card flat class="panel-card">
                    <div class="section-header d-flex align-center mb-4">
                        <!-- <h3 class="section-title mb-0">Automated Generation</h3> -->
                        <v-spacer />

                        <v-btn small color="primary" outlined class="ml-2" @click="generatePrompts(false)" :disabled="loadingPrompts || !generatedPrompts.length">
                            <v-icon left small>mdi-refresh</v-icon>
                            Load More
                        </v-btn>
                    </div>

                    <div v-if="!generatedPrompts.length && !loadingPrompts" class="empty-state">
                        <v-icon large color="primary" class="mb-2">mdi-creation</v-icon>
                        <div class="empty-title">No prompts generated yet</div>
                        <div class="empty-sub">Click Generate to create brand-aligned prompt ideas.</div>
                        <v-btn color="primary" class="mt-3" @click="generatePrompts"> <v-icon left small>mdi-play</v-icon>Generate Prompts </v-btn>
                    </div>

                    <v-skeleton-loader v-if="loadingPrompts && !generatedPrompts.length" class="mt-2" type="table"></v-skeleton-loader>

                    <v-data-table
                        v-if="generatedPrompts.length"
                        :headers="promptHeaders"
                        :items="generatedPrompts"
                        item-key="prompt_text"
                        show-select
                        v-model="selectedPrompts"
                        dense
                        class="elevation-1 mt-2 generated-table">
                        <template v-slot:top>
                            <div class="d-flex flex-wrap align-center px-2 py-2">
                                <v-btn small text color="primary" @click="toggleAllPrompts">
                                    <v-icon left small>
                                        {{ allPromptsSelected ? "mdi-checkbox-multiple-marked" : "mdi-checkbox-multiple-blank-outline" }}
                                    </v-icon>
                                    {{ allPromptsSelected ? "Unselect All" : "Select All" }}
                                </v-btn>
                                <v-divider vertical class="mx-3" />
                                <v-chip small outlined color="primary" class="mr-2"> {{ selectedPrompts.length }} selected </v-chip>
                                <v-spacer />
                                <v-btn small text color="primary" @click="copyPromptsToClipboard" :disabled="!selectedPrompts.length">
                                    <v-icon small left>mdi-content-copy</v-icon>
                                    Copy Selected
                                </v-btn>
                            </div>
                        </template>
                        <template v-slot:item.data-table-select="{ item, isSelected, select }">
                            <v-simple-checkbox :value="isSelected" :disabled="isPromptTooLong(item)" @input="select" />
                        </template>
                        <template v-slot:item.prompt_text="{ item }">
                            <span class="mono prompt-cell" :class="{ 'error--text': isPromptTooLong(item) }">{{ item.prompt_text }}</span>
                            <div v-if="isPromptTooLong(item)" class="prompt-toolong-hint error--text">
                                {{ item.prompt_text.trim().length }}/{{ maxPromptLength }} characters – too long, cannot be selected
                            </div>
                        </template>
                    </v-data-table>

                    <v-overlay :value="loadingPrompts" opacity="0.05">
                        <v-progress-circular indeterminate color="primary" />
                    </v-overlay>
                </v-card>
            </v-tab-item>

            <!-- Advanced Generation Tab -->
            <v-tab-item>
                <v-card flat class="panel-card advanced-panel">
                    <v-row>
                        <!-- SIDE (unverändert) -->
                        <v-col cols="12" md="4" class="pr-md-6">
                            <div class="sticky-col advanced-side">
                                <h3 class="section-title mb-1">Advanced Prompt Generation</h3>
                                <p class="side-sub mb-4">We create large volumes of high-quality, structured prompts based on:</p>

                                <!-- Metrics / Status -->
                                <div class="metrics-bar mb-4">
                                    <div class="metric">
                                        <div class="metric-label">Topics</div>
                                        <div class="metric-value">{{ mgmtThemes.length }}</div>
                                    </div>
                                    <div class="metric">
                                        <div class="metric-label">Personas</div>
                                        <div class="metric-value">{{ mgmtPersonas.length }}</div>
                                    </div>
                                    <div class="metric">
                                        <div class="metric-label">Stages</div>
                                        <div class="metric-value">{{ mgmtJourneyStages.length }}</div>
                                    </div>
                                    <div class="metric total" v-if="potentialTotal">
                                        <div class="metric-label">Combos</div>
                                        <div class="metric-value">{{ potentialTotal }}</div>
                                    </div>
                                </div>

                                <v-alert dense outlined border="left" colored-border type="info" class="mb-4 subtle-alert"> Topics × Personas × Stages → structured prompt space </v-alert>

                                <!-- Help -->
                                <v-card flat class="need-help-card mb-4 improved-help">
                                    <div class="d-flex align-start">
                                        <div class="help-icon-wrapper">
                                            <v-icon color="white" small>mdi-compass-outline</v-icon>
                                        </div>
                                        <div>
                                            <div class="need-help-heading">Need Help?</div>
                                            <div class="need-help-text">Book a call – we structure, normalize & deliver the tagged library for you.</div>
                                        </div>
                                    </div>
                                    <v-btn :href="bookingUrl" target="_blank" color="primary" block large class="mt-4">
                                        <v-icon left small>mdi-calendar-plus</v-icon>
                                        Book a Call
                                    </v-btn>
                                </v-card>
                            </div>
                        </v-col>

                        <!-- MAIN -->
                        <v-col cols="12" md="8">
                            <v-card outlined class="pa-4 mb-5 matrix-card">
                                <!-- Neuer Header mit Advanced Generate -->
                                <div class="d-flex align-center mb-3">
                                    <h4 class="mb-0 form-block-title">Input Matrix</h4>
                                    <v-spacer />
                                    <v-btn small color="primary" class="mr-2" :disabled="!potentialTotal || advancedLoading || potentialTotal === 0" @click="generateAdvancedPrompts">
                                        <v-icon left small>{{ advancedLoading ? "mdi-progress-clock" : "mdi-play-circle" }}</v-icon>
                                        Advanced Generate
                                    </v-btn>
                                    <v-btn v-if="advancedLoading" small text color="red darken-1" @click="cancelAdvanced"> <v-icon left x-small>mdi-close</v-icon>Cancel </v-btn>
                                </div>

                                <v-slide-y-transition>
                                    <div v-if="advancedLoading" class="advanced-progress-wrapper mb-3">
                                        <v-progress-linear :value="advancedProgressPercent" height="6" color="primary" striped rounded />
                                        <div class="progress-meta d-flex justify-space-between mt-1">
                                            <span class="progress-label"> Batch {{ advancedBatchIndex + 1 }} / {{ advancedBatchTotal }} </span>
                                            <span class="progress-label"> {{ advancedCollected }} Prompts collected </span>
                                        </div>
                                    </div>
                                </v-slide-y-transition>

                                <v-alert v-if="!advancedLoading && advancedLastMessage" dense type="success" class="mb-3 py-2 px-3 advanced-done-alert">
                                    <v-icon left small>mdi-check-circle</v-icon>{{ advancedLastMessage }}
                                </v-alert>

                                <!-- Bestehender Matrix-Inhalt (Felder) bleibt -->
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="matrix-head d-flex align-center">
                                            <div class="matrix-kpis d-flex align-center">
                                                <v-chip x-small class="mx-1 matrix-kpi">{{ mgmtThemes.length }} Topics</v-chip>
                                                <v-chip x-small class="mx-1 matrix-kpi">{{ mgmtPersonas.length }} Personas</v-chip>
                                                <v-chip x-small class="mx-1 matrix-kpi">{{ mgmtJourneyStages.length }} Stages</v-chip>
                                                <v-chip v-if="potentialTotal" x-small color="primary" outlined class="mx-1 matrix-kpi total">{{ potentialTotal }} Combos</v-chip>
                                            </div>
                                        </div>
                                        <div class="matrix-subline">Enter one value per line. We combine them into a structured prompt space.</div>
                                    </v-col>

                                    <!-- Gestapelte Felder -->
                                    <v-col cols="12">
                                        <div class="field-block stacked-field">
                                            <div class="field-label">
                                                <v-icon x-small class="mr-1" color="primary">mdi-tag-multiple</v-icon>
                                                Topics / Keywords
                                            </div>
                                            <v-textarea
                                                v-model="mgmtThemesText"
                                                auto-grow
                                                rows="5"
                                                outlined
                                                dense
                                                class="matrix-input"
                                                placeholder="e.g. pricing strategy, customer retention, onboarding"
                                                hide-details="auto" />
                                            <div class="field-hint">Use core thematic anchors (one per line).</div>
                                        </div>
                                    </v-col>

                                    <v-col cols="12">
                                        <div class="field-block stacked-field">
                                            <div class="field-label">
                                                <v-icon x-small class="mr-1" color="primary">mdi-account-multiple</v-icon>
                                                Buyer Personas
                                            </div>
                                            <v-textarea
                                                v-model="mgmtPersonasText"
                                                auto-grow
                                                rows="5"
                                                outlined
                                                dense
                                                class="matrix-input"
                                                placeholder="e.g. CFO, Head of Marketing, Sales Manager"
                                                hide-details="auto" />
                                            <div class="field-hint">Roles / archetypes influencing angle & framing.</div>
                                        </div>
                                    </v-col>

                                    <v-col cols="12">
                                        <div class="field-block stacked-field">
                                            <div class="field-label">
                                                <v-icon x-small class="mr-1" color="primary">mdi-timeline-clock</v-icon>
                                                Journey Stages
                                            </div>
                                            <v-textarea
                                                v-model="mgmtJourneyStagesText"
                                                auto-grow
                                                rows="5"
                                                outlined
                                                dense
                                                class="matrix-input"
                                                placeholder="Awareness, Consideration, Decision"
                                                hide-details="auto" />
                                            <div class="field-hint">Funnel / lifecycle checkpoints; keep naming consistent.</div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>
                    </v-row>
                    <!-- Neue Advanced Preview -->
                    <v-card outlined class="advanced-preview-card mb-5" v-if="advancedPreviewPrompts.length">
                        <div class="d-flex align-center mb-3 card-head-inline">
                            <h4 class="mb-0 form-block-title">Prompt Preview</h4>
                            <v-spacer />
                            <v-btn
                                small
                                text
                                color="primary"
                                class="mr-2"
                                @click="clearAdvancedFilters"
                                :disabled="!advancedFilterSearch && !advancedFilterTopic && !advancedFilterPersona && !advancedFilterStage">
                                <v-icon left x-small>mdi-filter-remove</v-icon>Filter Reset
                            </v-btn>
                            <v-btn small text color="red darken-1" @click="removeUnselectedAdvanced" :disabled="!unselectedAdvancedCount">
                                <v-icon left x-small>mdi-delete-outline</v-icon>
                                Entferne {{ unselectedAdvancedCount }} nicht ausgewählte
                            </v-btn>
                        </div>

                        <!-- Filterleiste -->
                        <v-row dense class="mb-3">
                            <v-col cols="12" md="3">
                                <v-select dense hide-details clearable :items="advancedTopics" label="Topic" v-model="advancedFilterTopic" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-select dense hide-details clearable :items="advancedPersonas" label="Persona" v-model="advancedFilterPersona" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-select dense hide-details clearable :items="advancedStages" label="Stage" v-model="advancedFilterStage" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field dense hide-details clearable label="Suche" v-model="advancedFilterSearch" prepend-inner-icon="mdi-magnify" />
                            </v-col>
                        </v-row>

                        <div class="preview-table-wrapper">
                            <v-data-table
                                :headers="advancedPreviewHeaders"
                                :items="filteredAdvancedPrompts"
                                item-key="prompt_text"
                                show-select
                                v-model="selectedPrompts"
                                dense
                                class="advanced-preview-table"
                                :items-per-page="10">
                                <template v-slot:top>
                                    <div class="d-flex flex-wrap align-center px-4 py-2 top-bar-inline">
                                        <v-btn small text color="primary" @click="toggleAllAdvanced">
                                            <v-icon left small>
                                                {{ allAdvancedSelected ? "mdi-checkbox-multiple-marked" : "mdi-checkbox-multiple-blank-outline" }}
                                            </v-icon>
                                            {{ allAdvancedSelected ? "Unselect All" : "Select All" }}
                                        </v-btn>
                                        <v-divider vertical class="mx-3" />
                                        <v-chip small outlined color="primary" class="mr-2"> {{ selectedAdvancedCount }} / {{ advancedPreviewPrompts.length }} selected </v-chip>
                                        <v-spacer />
                                        <v-btn small text color="primary" @click="copyAdvancedSelected" :disabled="!selectedAdvancedCount">
                                            <v-icon left small>mdi-content-copy</v-icon>
                                            Copy Selected
                                        </v-btn>
                                    </div>
                                </template>
                                <template v-slot:item.data-table-select="{ item, isSelected, select }">
                                    <v-simple-checkbox :value="isSelected" :disabled="isPromptTooLong(item)" @input="select" />
                                </template>
                                <template v-slot:item.prompt_text="{ item }">
                                    <span class="mono preview-cell" :class="{ 'error--text': isPromptTooLong(item) }">{{ item.prompt_text }}</span>
                                    <div v-if="isPromptTooLong(item)" class="prompt-toolong-hint error--text">
                                        {{ item.prompt_text.trim().length }}/{{ maxPromptLength }} characters – too long, cannot be selected
                                    </div>
                                </template>
                            </v-data-table>
                        </div>
                    </v-card>
                </v-card>
            </v-tab-item>
        </v-tabs-items>
    </div>
</template>

<script>
    import FileUpload from "./FileUpload"
    import axios from "axios"
    import aiFunctions from "../content-suite/editor/aiFunctions.js"
    // import Papa from "papaparse" // entfernen

    let intervalId = null
    const MAX_PROMPT_LENGTH = 700

    export default {
        props: { project: Object, mountedOn: String },
        components: { FileUpload },
        data() {
            return {
                maxPromptLength: MAX_PROMPT_LENGTH,
                tab: 0,
                exampleFile:
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/exports/example-file.csv",
                uploadUrl:
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/project/add/keywords/upload",
                uploadVia: "file",
                kw_textfield: "",
                loadingPrompts: false,
                generatedPrompts: [],
                selectedPrompts: [],
                promptHeaders: [{ text: "Prompt", value: "prompt_text" }],
                lastSignature: "",
                bookingUrl: "https://quaro.io/demo/",
                mgmtThemesText: "",
                mgmtPersonasText: "",
                mgmtJourneyStagesText: "",
                conciergeHeaders: [
                    { text: "Topic", value: "theme", sortable: false, width: 140 },
                    { text: "Persona", value: "persona", sortable: false, width: 140 },
                    { text: "Stage", value: "stage", sortable: false, width: 140 },
                    { text: "Example Prompt", value: "example_prompt", sortable: false }
                ],
                // --- Advanced Batch Generation State ---
                advancedLoading: false,
                advancedCancel: false,
                advancedBatchIndex: 0,
                advancedBatchTotal: 0,
                advancedCollected: 0,
                advancedLastMessage: "",
                advancedCurrentThread: null,
                advancedCurrentRun: null,
                // Neu: Zähler für abgeschlossene Batches
                advancedCompleted: 0,
                // Optional: zentral steuerbare Parallelität
                advancedConcurrency: 1,
                promptGenEndpoint:
                    process.env.VUE_APP_PROMPT_GEN_ENDPOINT ||
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/ai/prompts/generate",
                advancedPreviewHeaders: [
                    { text: "Prompt", value: "prompt_text", sortable: false },
                    { text: "Topic", value: "topic", sortable: false, width: 120 },
                    { text: "Persona", value: "persona", sortable: false, width: 110 },
                    { text: "Stage", value: "journey_stage", sortable: false, width: 110 }
                ],
                advancedFilterSearch: "",
                advancedFilterTopic: null,
                advancedFilterPersona: null,
                advancedFilterStage: null,
                // CSV Upload
                csvFile: null,
                csvHeaders: [],
                csvRows: [], // raw objects from parser
                csvError: "",
                csvWarning: "",
                csvMapping: { keyword: "", tags: "" },
                tagSeparator: ",",
                dedupeTags: true,
                tagSeparators: [
                    { label: "Comma (,)", value: "," },
                    { label: "Semicolon (;)", value: ";" },
                    { label: "Pipe (|)", value: "|" }
                ],
                csvSelected: [],
                csvSelectedDetailed: [], // [{ keyword, tags: [] }]
                // Credits / Kostenschätzung
                promptCredits: { total: 0, used: 0, remaining: 0 },
                creditsLoading: false,
                creditsLoaded: false
            }
        },
        computed: {
            cleaned_keywords() {
                try {
                    let input = this.splitInput(this.kw_textfield)
                    if (!input.length) return []
                    if (!this.validateInput(input)) return []
                    input = this.$helpers.replaceCharactersForSerpAPI(input)
                    return [...new Set(input.filter((e) => e && e.trim().length))]
                } catch {
                    return []
                }
            },
            selectableGeneratedPrompts() {
                return this.generatedPrompts.filter((p) => !this.isPromptTooLong(p))
            },
            allPromptsSelected() {
                return this.selectableGeneratedPrompts.length > 0 && this.selectedPrompts.length === this.selectableGeneratedPrompts.length
            },
            allPrompts() {
                const merged = [
                    ...this.selectedPrompts.map((p) => (p.prompt_text || "").trim()),
                    ...this.cleaned_keywords,
                    // CSV-Auswahl ebenfalls berücksichtigen
                    ...this.csvSelected
                ].filter((p) => p && p.length <= this.maxPromptLength)
                const unique = [...new Set(merged)]
                const sig = unique.join("||")
                if (sig !== this.lastSignature) {
                    this.lastSignature = sig
                    this.$emit("promptsAdded", unique)
                }
                return unique
            },
            mgmtThemes() {
                return this.parseLines(this.mgmtThemesText)
            },
            mgmtPersonas() {
                return this.parseLines(this.mgmtPersonasText)
            },
            mgmtJourneyStages() {
                return this.parseLines(this.mgmtJourneyStagesText)
            },
            potentialTotal() {
                return this.mgmtThemes.length * this.mgmtPersonas.length * this.mgmtJourneyStages.length
            },
            combinationPreview() {
                if (!this.potentialTotal) return []
                const out = []
                const limit = 100
                for (const theme of this.mgmtThemes) {
                    for (const persona of this.mgmtPersonas) {
                        for (const stage of this.mgmtJourneyStages) {
                            if (out.length >= limit) return out
                            out.push({
                                theme,
                                persona,
                                stage,
                                example_prompt: `Generate a high-quality prompt for ${persona} in the ${stage} stage about "${theme}".`
                            })
                        }
                    }
                }
                return out
            },
            conciergeSpecJson() {
                if (!this.potentialTotal) return ""
                return JSON.stringify(
                    {
                        company_id: localStorage.company_id,
                        spec_version: 1,
                        topics: this.mgmtThemes,
                        personas: this.mgmtPersonas,
                        journey_stages: this.mgmtJourneyStages,
                        total_combinations: this.potentialTotal,
                        notes: "Matrix used to generate a tagged prompt library."
                    },
                    null,
                    2
                )
            },
            advancedProgressPercent() {
                if (!this.advancedBatchTotal) return 0
                // Neu: Fortschritt basiert auf abgeschlossenen Batches
                return Math.min(100, (this.advancedCompleted / this.advancedBatchTotal) * 100)
            },
            advancedPreviewPrompts() {
                // Nur Advanced-Quelle
                return this.generatedPrompts.filter((p) => p.source === "advanced")
            },
            advancedTopics() {
                return [...new Set(this.advancedPreviewPrompts.map((p) => p.topic).filter(Boolean))].sort()
            },
            advancedPersonas() {
                return [...new Set(this.advancedPreviewPrompts.map((p) => p.persona).filter(Boolean))].sort()
            },
            advancedStages() {
                return [...new Set(this.advancedPreviewPrompts.map((p) => p.journey_stage).filter(Boolean))].sort()
            },
            filteredAdvancedPrompts() {
                let arr = this.advancedPreviewPrompts
                if (this.advancedFilterTopic) arr = arr.filter((p) => p.topic === this.advancedFilterTopic)
                if (this.advancedFilterPersona) arr = arr.filter((p) => p.persona === this.advancedFilterPersona)
                if (this.advancedFilterStage) arr = arr.filter((p) => p.journey_stage === this.advancedFilterStage)
                if (this.advancedFilterSearch) {
                    const q = this.advancedFilterSearch.toLowerCase()
                    arr = arr.filter(
                        (p) =>
                            (p.prompt_text || "").toLowerCase().includes(q) ||
                            (p.topic || "").toLowerCase().includes(q) ||
                            (p.persona || "").toLowerCase().includes(q) ||
                            (p.journey_stage || "").toLowerCase().includes(q)
                    )
                }
                return arr
            },
            selectedAdvancedCount() {
                const set = new Set(this.selectedPrompts.map((p) => p.prompt_text))
                return this.advancedPreviewPrompts.filter((p) => set.has(p.prompt_text)).length
            },
            selectableAdvancedPrompts() {
                return this.advancedPreviewPrompts.filter((p) => !this.isPromptTooLong(p))
            },
            allAdvancedSelected() {
                return this.selectableAdvancedPrompts.length > 0 && this.selectedAdvancedCount === this.selectableAdvancedPrompts.length
            },
            unselectedAdvancedCount() {
                return this.advancedPreviewPrompts.length - this.selectedAdvancedCount
            },
            allPromptsDetailed() {
                // Quellen: selectedPrompts, cleaned_keywords, csvSelectedDetailed
                const map = new Map() // keyword -> Set(tags)
                const add = (keyword, tagsArr) => {
                    const k = (keyword || "").trim()
                    if (!k || k.length > this.maxPromptLength) return
                    const tags = (tagsArr || []).map((t) => (t || "").toString().trim()).filter(Boolean)
                    if (!map.has(k)) map.set(k, new Set(tags))
                    else tags.forEach((t) => map.get(k).add(t))
                }

                // 1) Ausgewählte generierte Prompts mit impliziten Tags
                for (const p of this.selectedPrompts) {
                    if (!p?.prompt_text) continue
                    add(p.prompt_text, [p.topic, p.persona, p.journey_stage])
                }
                // 2) Manuell eingefügte (ohne Tags)
                for (const plain of this.cleaned_keywords) add(plain, [])
                // 3) CSV-Auswahl (mit Tags)
                for (const o of this.csvSelectedDetailed) add(o.keyword, o.tags)

                return Array.from(map.entries()).map(([keyword, set]) => ({ keyword, tags: Array.from(set) }))
            },
            previewRows() {
                return this.toPromptObjects(this.csvRows).slice(0, 10)
            },
            canApplyCSV() {
                return this.csvRows.length > 0 && this.csvMapping.keyword && this.csvAppliedCount > 0
            },
            csvAppliedCount() {
                return this.toPromptObjects(this.csvRows).filter((o) => !o.tooLong).length
            },
            pasteOverLongLines() {
                return this.splitInput(this.kw_textfield)
                    .map((line, idx) => ({ idx: idx + 1, length: line.length }))
                    .filter((l) => l.length > this.maxPromptLength)
            },
            // Anzahl der Plattformen des Projekts (inkl. Google)
            platformCount() {
                const base = Array.isArray(this.project?.platforms) ? this.project.platforms.length : 0
                return base + (this.project?.google ? 1 : 0)
            },
            // Läufe pro Monat je nach Update-Frequenz des Projekts
            intervalRunsPerMonth() {
                const interval = Number(this.project?.interval)
                if (interval === 28 || interval === 30 || interval === 31) return 1 // monatlich
                if (interval === 14) return 2 // alle 2 Wochen
                if (interval === 7) return 4 // wöchentlich
                return 30 // täglich (Standard)
            },
            intervalLabel() {
                const interval = Number(this.project?.interval)
                if (interval === 28 || interval === 30 || interval === 31) return "monthly"
                if (interval === 14) return "every 2 weeks"
                if (interval === 7) return "weekly"
                return "daily"
            },
            // Geschätzte Credit-Kosten der aktuell ausgewählten Prompts pro Monat
            estimatedCreditCost() {
                return this.allPrompts.length * this.intervalRunsPerMonth * this.platformCount
            },
            creditsExceeded() {
                return this.creditsLoaded && this.estimatedCreditCost > this.promptCredits.remaining
            },
            creditsRemainingAfter() {
                return this.promptCredits.remaining - this.estimatedCreditCost
            }
        },
        watch: {
            kw_textfield() {
                this.triggerEmit()
            },
            selectedPrompts: {
                deep: true,
                handler() {
                    this.triggerEmit()
                }
            }
        },
        methods: {
            toggleAllAdvanced() {
                if (this.allAdvancedSelected) {
                    const advSet = new Set(this.advancedPreviewPrompts.map((p) => p.prompt_text))
                    this.selectedPrompts = this.selectedPrompts.filter((p) => !advSet.has(p.prompt_text))
                } else {
                    const existing = new Set(this.selectedPrompts.map((p) => p.prompt_text))
                    const add = this.selectableAdvancedPrompts.filter((p) => !existing.has(p.prompt_text))
                    this.selectedPrompts = [...this.selectedPrompts, ...add]
                }
                this.triggerEmit()
            },
            triggerEmit() {
                // Beibehaltung altes Event (Strings)
                void this.allPrompts
                // Neues detailliertes Event (Objekte mit optionalen Tags)
                this.$emit("promptsAddedDetailed", this.allPromptsDetailed)
            },
            clearInput() {
                this.kw_textfield = ""
            },
            fileLoaded(f) {
                this.$emit("fileLoaded", f)
            },
            createNotification(m, i, c) {
                this.$emit("createNotification", m, i, c)
            },
            textChanged() {
                this.triggerEmit()
            },
            splitInput(t) {
                return (t || "")
                    .split(/\r?\n/)
                    .map((v) => v.trim())
                    .filter((v) => v)
            },
            validateInput(input) {
                const errors = this.$helpers.validateSerpApiKeywords(input)
                if (errors.length) {
                    const lines = errors.map((e) => parseInt(e.index) + 1).join(",")
                    this.createNotification(`Prompts must be at most ${this.maxPromptLength} characters. Please fix line(s): ${lines}`, "mdi-alert-circle-outline", "#FCBA03")
                    return false
                }
                return true
            },
            isPromptTooLong(item) {
                return ((item && item.prompt_text) || "").trim().length > this.maxPromptLength
            },
            addFromClipboard() {
                if (this.$store.state.clipboard_keywords?.length) {
                    this.kw_textfield = this.$store.state.clipboard_keywords.map((k) => k.keyword).join("\n")
                }
            },
            extractPromptArray(parsed) {
                if (Array.isArray(parsed)) return parsed
                if (parsed && typeof parsed === "object") {
                    return parsed.prompts || parsed.data || parsed.items || parsed.results || parsed.output || []
                }
                return []
            },
            async callPromptGenerator(payload, expectArray = true) {
                const { data } = await this.$helpers.axiosPost(this.promptGenEndpoint, { payload }, 300000)
                if (!expectArray) return data
                return this.extractPromptArray(data)
            },
            async generatePrompts(reset = true) {
                this.loadingPrompts = true
                if (reset) this.generatedPrompts = []
                try {
                    const url = process.env.VUE_APP_APIURL + "/v2/sources/content-asisstant/adhocrun"
                    const authsplits = process.env.VUE_APP_AUTH.split(":")
                    const post_data = {
                        company_id: localStorage.company_id,
                        assistant: "prompt_generator",
                        message: JSON.stringify({
                            brand: this.project?.brand?.brand_name || "",
                            language: this.project?.langloc?.language_code || "en",
                            prompt_count: 50
                        })
                    }
                    const response_data = await axios.post(url, post_data, {
                        auth: { username: authsplits[0], password: authsplits[1] }
                    })
                    if (response_data.data.thread_id && response_data.data.run_id) {
                        this.checkRun(response_data.data.thread_id, response_data.data.run_id, reset)
                    } else {
                        this.loadingPrompts = false
                    }
                } catch {
                    this.createNotification("Fehler beim Generieren der Prompts", "mdi-alert-circle-outline", "error")
                    this.loadingPrompts = false
                }
            },

            checkRun(thread, run, reset = true) {
                if (!thread || !run) return
                if (intervalId) clearInterval(intervalId)
                intervalId = setInterval(async () => {
                    try {
                        const res = await aiFunctions.checkRun(thread, run)
                        if (res.data.status === "completed") {
                            clearInterval(intervalId)
                            try {
                                const raw = res.data.messages?.data?.[0]?.content?.[0]?.text?.value
                                const parsed = JSON.parse(raw)
                                const incoming = this.extractPromptArray(parsed)
                                if (reset) {
                                    this.generatedPrompts = incoming
                                } else {
                                    const existing = new Set(this.generatedPrompts.map((p) => p.prompt_text))
                                    this.generatedPrompts = [...this.generatedPrompts, ...incoming.filter((p) => !existing.has(p.prompt_text))]
                                }
                                const overLong = incoming.filter((p) => this.isPromptTooLong(p)).length
                                if (overLong) {
                                    this.createNotification(
                                        `${overLong} generated prompt${overLong > 1 ? "s" : ""} exceed ${this.maxPromptLength} characters and cannot be selected`,
                                        "mdi-alert-circle-outline",
                                        "#FCBA03"
                                    )
                                }
                            } catch {
                                // ignore parsing errors
                            }
                            this.loadingPrompts = false
                            this.triggerEmit()
                        }
                    } catch {
                        // ignore polling errors
                    }
                }, 600)
            },
            toggleAllPrompts() {
                this.selectedPrompts = this.allPromptsSelected ? [] : [...this.selectableGeneratedPrompts]
            },
            copyPromptsToClipboard() {
                const text = this.allPrompts.join("\n")
                navigator.clipboard
                    .writeText(text)
                    .then(() => this.createNotification("Prompts copied", "mdi-content-copy", "primary"))
                    .catch(() => this.createNotification("Copy failed", "mdi-alert-circle-outline", "error"))
            },
            parseLines(txt) {
                return (txt || "")
                    .split(/\r?\n/)
                    .map((l) => l.trim())
                    .filter((l) => l)
            },
            copyConciergeSpec() {
                if (!this.conciergeSpecJson) return
                navigator.clipboard
                    .writeText(this.conciergeSpecJson)
                    .then(() => this.createNotification("Spec copied", "mdi-content-copy", "primary"))
                    .catch(() => this.createNotification("Copy failed", "mdi-alert-circle-outline", "error"))
            },
            buildAdvancedCombos() {
                const out = []
                for (const theme of this.mgmtThemes) {
                    for (const persona of this.mgmtPersonas) {
                        for (const stage of this.mgmtJourneyStages) {
                            out.push({ topic: theme, persona, stage })
                        }
                    }
                }
                return out
            },
            async generateAdvancedPrompts() {
                const combos = this.buildAdvancedCombos()
                if (!combos.length) {
                    this.createNotification("Matrix ist leer", "mdi-alert-circle-outline", "warning")
                    return
                }
                this.advancedLoading = true
                this.advancedCancel = false
                this.advancedBatchIndex = 0
                this.advancedCompleted = 0
                this.advancedCollected = 0
                this.advancedLastMessage = ""
                const batchSize = 20 // ggf. 30–40 testen
                const batches = []
                for (let i = 0; i < combos.length; i += batchSize) {
                    batches.push(combos.slice(i, i + batchSize))
                }
                this.advancedBatchTotal = batches.length

                // Parallel mit Limit
                await this.runBatchesWithConcurrency(batches, this.advancedConcurrency)

                if (this.advancedCancel) {
                    this.createNotification("Advanced Generation abgebrochen", "mdi-cancel", "warning")
                    this.advancedLastMessage = "Abgebrochen – " + this.advancedCollected + " Prompts gesammelt."
                } else {
                    this.advancedLastMessage = "Fertig – " + this.advancedCollected + " Prompts gesammelt."
                }

                const overLongCount = this.advancedPreviewPrompts.filter((p) => this.isPromptTooLong(p)).length
                if (overLongCount) {
                    this.createNotification(
                        `${overLongCount} generated prompt${overLongCount > 1 ? "s" : ""} exceed ${this.maxPromptLength} characters and cannot be selected`,
                        "mdi-alert-circle-outline",
                        "#FCBA03"
                    )
                }

                this.advancedLoading = false
                this.advancedCurrentRun = null
                this.advancedCurrentThread = null
                this.triggerEmit()
            },
            // Neu: Concurrency-Runner
            async runBatchesWithConcurrency(batches, concurrency = 3) {
                const self = this
                let index = 0

                async function worker(workerId) {
                    while (!self.advancedCancel && index < batches.length) {
                        const myIndex = index++
                        // Für Anzeige: letzter gestarteter Index
                        self.advancedBatchIndex = myIndex
                        try {
                            await self.runAdvancedBatch(batches[myIndex], myIndex + 1, batches.length)
                        } catch (e) {
                            // Fehler pro Batch bereits im runAdvancedBatch behandelt
                        } finally {
                            self.advancedCompleted++
                        }
                    }
                }

                const workers = []
                const maxWorkers = Math.min(concurrency, batches.length)
                for (let i = 0; i < maxWorkers; i++) {
                    workers.push(worker(i + 1))
                }
                await Promise.all(workers)
            },
            async runAdvancedBatch(batch, batchNumber, totalBatches) {
                try {
                    const batchPayload = {
                        brand: this.project?.brand?.brand_name || "",
                        language: (this.project?.langloc?.language_code || "en").toUpperCase(),
                        combinations: batch
                    }
                    const arr = await this.callPromptGenerator(batchPayload)
                    const cleaned = (Array.isArray(arr) ? arr : [])
                        .map((p) =>
                            typeof p === "string"
                                ? { prompt_text: p }
                                : {
                                      prompt_text: p.prompt_text,
                                      topic: p.topic,
                                      persona: p.persona,
                                      journey_stage: p.journey_stage
                                  }
                        )
                        .filter((p) => p.prompt_text)
                        .map((p) => ({ ...p, source: "advanced" }))
                    const existing = new Set(this.generatedPrompts.map((p) => p.prompt_text))
                    const toAdd = cleaned.filter((p) => !existing.has(p.prompt_text))
                    this.generatedPrompts = [...this.generatedPrompts, ...toAdd]
                    this.advancedCollected += toAdd.length
                } catch {
                    this.createNotification(`Batch Fehler (${batchNumber})`, "mdi-alert-circle-outline", "error")
                }
            },
            pollAdvancedRun(thread, run) {
                return new Promise((resolve) => {
                    const pollId = setInterval(async () => {
                        if (this.advancedCancel) {
                            clearInterval(pollId)
                            return resolve()
                        }
                        try {
                            const res = await aiFunctions.checkRun(thread, run)
                            if (res.data.status === "completed") {
                                clearInterval(pollId)
                                try {
                                    const raw = res.data.messages?.data?.[0]?.content?.[0]?.text?.value
                                    const parsed = JSON.parse(raw)
                                    const arr = this.extractPromptArray(parsed)
                                    const existing = new Set(this.generatedPrompts.map((p) => p.prompt_text))
                                    const cleaned = arr
                                        .map((p) => {
                                            if (typeof p === "string") return { prompt_text: p }
                                            return { prompt_text: p.prompt_text }
                                        })
                                        .filter((p) => p.prompt_text && !existing.has(p.prompt_text))
                                    this.generatedPrompts = [...this.generatedPrompts, ...cleaned]
                                    this.advancedCollected += cleaned.length
                                } catch {
                                    // ignore parse
                                }
                                this.advancedCurrentRun = null
                                resolve()
                            }
                        } catch {
                            // swallow poll error
                        }
                    }, 700)
                })
            },
            // CSV lesen
            async handleCSVFile(file) {
                this.csvError = ""
                this.csvWarning = ""
                this.csvHeaders = []
                this.csvRows = []
                this.csvMapping = { keyword: "", tags: "" }

                if (!file) return
                try {
                    const text = await file.text()
                    const parsed = this.parseCSV(text)
                    if (!parsed || !parsed.data || !parsed.data.length) {
                        this.csvError = "CSV is empty or could not be parsed."
                        return
                    }
                    // Header + Rows
                    const { headers, rows } = parsed
                    this.csvHeaders = headers
                    this.autoMap(headers)
                    this.csvRows = rows
                } catch (e) {
                    console.error(e)
                    this.csvError = "Failed to read CSV file."
                }
            },

            parseCSV(text) {
                if (!text) return { headers: [], rows: [], data: [] }

                // BOM entfernen und Zeilenenden normalisieren
                if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
                text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n")

                const candidates = [";", ",", "\t", "|"] // Semikolon zuerst (bevorzugt)

                const splitWithQuotes = (line, delim) => {
                    const out = []
                    let cur = ""
                    let inQuotes = false
                    for (let i = 0; i < line.length; i++) {
                        const ch = line[i]
                        if (ch === '"') {
                            if (inQuotes && line[i + 1] === '"') {
                                cur += '"'
                                i++
                            } else {
                                inQuotes = !inQuotes
                            }
                        } else if (ch === delim && !inQuotes) {
                            out.push(cur)
                            cur = ""
                        } else {
                            cur += ch
                        }
                    }
                    out.push(cur)
                    return out
                }

                // Delimiter-Sniffing: Semikolon bevorzugen
                const firstLine = text.split("\n")[0] || ""
                let bestDelim = ";"
                let bestCount = splitWithQuotes(firstLine, bestDelim).length
                for (const d of candidates) {
                    const cnt = splitWithQuotes(firstLine, d).length
                    if (cnt > bestCount) {
                        bestCount = cnt
                        bestDelim = d
                    }
                }
                const delim = bestDelim

                // Vollparser (char-by-char)
                const rows = []
                let row = []
                let field = ""
                let inQuotes = false

                for (let i = 0; i < text.length; i++) {
                    const ch = text[i]
                    if (ch === '"') {
                        if (inQuotes && text[i + 1] === '"') {
                            field += '"'
                            i++
                        } else {
                            inQuotes = !inQuotes
                        }
                    } else if (ch === delim && !inQuotes) {
                        row.push(field)
                        field = ""
                    } else if (ch === "\n" && !inQuotes) {
                        row.push(field)
                        rows.push(row)
                        row = []
                        field = ""
                    } else {
                        field += ch
                    }
                }
                if (field.length > 0 || row.length > 0) {
                    row.push(field)
                    rows.push(row)
                }

                if (!rows.length) return { headers: [], rows: [], data: [] }

                const headers = rows[0].map((h) => (h || "").trim())
                const dataRows = rows.slice(1).map((r) => {
                    const obj = {}
                    headers.forEach((h, idx) => (obj[h] = (r[idx] ?? "").trim()))
                    return obj
                })
                const cleanRows = dataRows.filter((o) => Object.values(o).some((v) => (v || "").length))
                return { headers, rows: cleanRows, data: cleanRows }
            },

            autoMap(headers) {
                const lower = (s) => (s || "").toString().toLowerCase()
                const keywordCol = headers.find((h) => ["prompt", "keyword", "keywords"].includes(lower(h))) || headers[0] || ""
                const tagsCol = headers.find((h) => ["tags", "label", "labels"].includes(lower(h))) || ""
                this.csvMapping.keyword = keywordCol
                this.csvMapping.tags = tagsCol
            },

            normPrompt(s) {
                return (s || "").toString().trim()
            },
            splitTags(s) {
                if (!s) return []
                const raw = (s || "")
                    .toString()
                    .split(this.tagSeparator)
                    .map((t) => t.trim())
                    .filter(Boolean)
                if (!this.dedupeTags) return raw
                return Array.from(new Set(raw))
            },

            toPromptObjects(rows) {
                if (!Array.isArray(rows)) return []
                const keywordKey = this.csvMapping.keyword
                const tagsKey = this.csvMapping.tags
                const out = []
                for (const r of rows) {
                    const keyword = this.normPrompt(r[keywordKey])
                    if (!keyword) continue
                    const tags = tagsKey ? this.splitTags(r[tagsKey]) : []
                    out.push({ keyword, tags, tooLong: keyword.length > this.maxPromptLength })
                }
                // Dedupe nach keyword
                const seen = new Set()
                return out.filter((o) => {
                    if (seen.has(o.keyword)) return false
                    seen.add(o.keyword)
                    return true
                })
            },

            applyCSV() {
                this.csvError = ""
                this.csvWarning = ""
                const allObjs = this.toPromptObjects(this.csvRows)
                const objs = allObjs.filter((o) => !o.tooLong)
                const skipped = allObjs.length - objs.length
                if (!objs.length) {
                    this.csvError = skipped ? `All ${skipped} prompt(s) exceed ${this.maxPromptLength} characters and were skipped.` : "No valid prompts found."
                    return
                }
                // Nur als Auswahl übernehmen (nicht ins Textfeld)
                this.csvSelectedDetailed = objs.map(({ keyword, tags }) => ({ keyword, tags }))
                this.csvSelected = objs.map((o) => o.keyword)

                // Events an Parent (inkl. Tags)
                this.triggerEmit()

                if (skipped) this.csvWarning = `${skipped} prompt(s) skipped: longer than ${this.maxPromptLength} characters.`
                this.$emit("createNotification", `Imported ${objs.length} prompts from CSV`, "mdi-file-delimited", "primary")
            },
            downloadTemplate() {
                // Spaltentrenner: ;  | Tags: Komma
                const tpl = "prompt;tags\nMy first prompt;brand, intent\nAnother prompt;feature, urgent\n"
                const blob = new Blob([tpl], { type: "text/csv;charset=utf-8;" })
                const url = URL.createObjectURL(blob)
                const a = document.createElement("a")
                a.href = url
                a.download = "prompts_template.csv"
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
            },
            formatNumber(value) {
                const n = Number(value)
                if (!Number.isFinite(n)) return "0"
                return n.toLocaleString("de-DE")
            },
            async fetchPromptCredits() {
                this.creditsLoading = true
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/credits/prompts"
                    const post_data = { company_id: localStorage.company_id }
                    const res = await this.$helpers.axiosPost(url, post_data)
                    if (res?.data?.success) {
                        this.promptCredits = {
                            total: Number(res.data.total) || 0,
                            used: Number(res.data.used) || 0,
                            remaining: Number(res.data.remaining) || 0
                        }
                        this.creditsLoaded = true
                    }
                } catch (e) {
                    console.error("fetchPromptCredits error", e)
                } finally {
                    this.creditsLoading = false
                }
            }
        },
        mounted() {
            this.fetchPromptCredits()
        },
        beforeDestroy() {
            if (intervalId) clearInterval(intervalId)
        },
        beforeUnmount() {
            if (intervalId) clearInterval(intervalId)
        }
    }
</script>

<style scoped lang="scss">
    .add-prompts-root {
        background: #f8f9fb;
        min-height: 100%;
    }

    /* Header */
    .header-slab {
        background: linear-gradient(135deg, #f6f8fa 0%, #ffffff 70%);
        padding: 28px 40px 24px;
        border-bottom: 1px solid #eceff2;
    }
    .page-title {
        font-weight: 300;
        font-size: 26px;
        letter-spacing: 0.5px;
        margin: 0 0 4px;
    }
    .page-subtitle {
        font-size: 14px;
        color: #616b74;
    }

    /* Credits-Panel */
    .credits-panel {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px 0;
        background: #ffffff;
        border: 1px solid #e3e7eb;
        border-radius: 12px;
        padding: 10px 16px;
    }
    .credits-panel__item {
        display: flex;
        align-items: center;
    }
    .credits-panel__label {
        font-size: 12px;
        color: #6b7580;
        margin-right: 8px;
    }
    .credits-panel__value {
        font-size: 15px;
        font-weight: 600;
        color: #1f2933;
    }
    .credits-panel__divider {
        height: 22px;
        align-self: center;
    }
    .credits-panel__alert {
        font-size: 13px;
    }

    /* Tabs */
    .elevated-tabbar {
        border-bottom: 1px solid #e3e7eb;
        box-shadow: 0 1px 2px rgba(20, 30, 40, 0.04);
        background: #fff;
    }

    /* Panels */
    .panel-card {
        padding: 28px 32px 40px;
    }

    /* Sections */
    .section-header {
        border-bottom: 1px solid #eceff2;
        padding-bottom: 10px;
    }
    .section-title {
        font-size: 18px;
        font-weight: 500;
        letter-spacing: 0.2px;
    }

    /* Generated table */
    .generated-table {
        border: 1px solid #e5e9ed;
        border-radius: 6px;
        overflow: hidden;
    }
    .prompt-cell {
        line-height: 1.3;
        font-size: 13px;
    }
    .prompt-toolong-hint,
    .csv-toolong-hint,
    .paste-toolong-hint {
        font-size: 11px;
        line-height: 1.3;
    }

    /* Empty State */
    .empty-state {
        text-align: center;
        padding: 40px 20px 30px;
        border: 1px dashed #d0d6dc;
        border-radius: 8px;
        background: #fcfdfe;
    }
    .empty-title {
        font-weight: 600;
        font-size: 15px;
        margin-bottom: 4px;
    }
    .empty-sub {
        font-size: 13px;
        color: #67727d;
    }

    /* Advanced */
    .sticky-col {
        position: sticky;
        top: 20px;
    }
    .need-help-card {
        background: #ffffff;
        border: 1px solid #e2e7ec;
        border-radius: 10px;
        padding: 18px 18px 20px;
        box-shadow: 0 2px 4px -2px rgba(20, 30, 40, 0.08), 0 4px 16px -4px rgba(20, 30, 40, 0.06);
    }
    .gradient-badge {
        background: linear-gradient(135deg, #1976d2, #42a5f5);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 10px -2px rgba(25, 118, 210, 0.4);
    }
    .need-help-heading {
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        color: #2f3a44;
        margin-bottom: 4px;
    }
    .need-help-text {
        font-size: 13px;
        line-height: 1.35;
        color: #59636c;
    }
    .form-block-title {
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 0.3px;
    }
    .placeholder-text {
        font-size: 13px;
        color: #6b7680;
        padding: 8px 4px 4px;
    }

    /* Chips / Tags */
    .chips-row label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        color: #5a636d;
        margin-bottom: 4px;
    }
    .persona-chip {
        background: #ede7f6 !important;
        color: #4a3785;
    }
    .stage-chip {
        background: #e3f4e8 !important;
        color: #295f38;
    }

    /* Table in combinations */
    .combo-table {
        border: 1px solid #e5e9ed;
        border-radius: 8px;
        overflow: hidden;
    }
    .example-cell {
        font-size: 12.5px;
        line-height: 1.25;
    }

    /* Utilities */
    .mono {
        font-family: Menlo, SFMono-Regular, Consolas, "Liberation Mono", monospace;
    }
    .mono-input textarea {
        font-family: Menlo, monospace;
        font-size: 13px;
    }

    /* Scroll sticky safety */
    @media (max-width: 960px) {
        .sticky-col {
            position: static;
        }
    }

    .advanced-panel {
        background: #fafbfc;
    }

    .advanced-side {
        border-left: 3px solid #009f89;
        padding-left: 14px;
    }

    .side-sub {
        font-size: 13px;
        line-height: 1.4;
        color: #5f6973;
    }

    .metrics-bar {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
        gap: 8px;
    }
    .metric {
        background: #ffffff;
        border: 1px solid #e3e8ed;
        border-radius: 8px;
        padding: 8px 10px 7px;
        text-align: center;
        position: relative;
    }
    .metric-label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        color: #5d6974;
        margin-bottom: 2px;
    }
    .metric-value {
        font-size: 16px;
        font-weight: 600;
        color: #22313c;
        line-height: 1.1;
    }
    .metric.total {
        background: #f1f8ff;
        border-color: #b3dafc;
    }

    .subtle-alert {
        font-size: 12.5px;
        line-height: 1.25;
    }
    .advanced-preview-table {
        border: 1px solid #e2e7ec;
        border-radius: 10px;
        overflow: hidden;
    }
    .preview-cell {
        font-size: 12.5px;
        line-height: 1.3;
    }
    .improved-help {
        border: 1px solid #dbe4ec;
        background: linear-gradient(145deg, #ffffff 0%, #f5f9fc 100%);
    }
    .help-icon-wrapper {
        background: linear-gradient(135deg, #1565c0, #42a5f5);
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 10px -2px rgba(18, 94, 168, 0.35);
        margin-right: 14px;
    }

    .matrix-card,
    .preview-card {
        background: #ffffff;
        border: 1px solid #e2e7ec;
        border-radius: 12px;
    }

    .matrix-head {
        padding: 4px 0 6px;
    }
    .matrix-head-title {
        font-size: 13.5px;
        font-weight: 600;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        color: #2d3942;
    }
    .matrix-subline {
        font-size: 12px;
        color: #6a747c;
        margin: -2px 0 14px;
    }

    .matrix-kpis .v-chip.matrix-kpi {
        background: #f2f5f7;
        font-weight: 600;
        font-size: 10.5px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #42505a;
        border: 1px solid #e1e6ea;
        height: 22px;
    }
    .matrix-kpis .v-chip.matrix-kpi.total {
        background: #f0f7ff;
        border-color: #c4e1fa;
        color: #0d4975;
    }

    .field-block {
        background: #ffffff;
        border: 1px solid #e3e8ed;
        border-radius: 10px;
        padding: 14px 14px 12px;
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        box-shadow: 0 2px 4px -2px rgba(15, 30, 45, 0.04);
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    .field-block:hover {
        border-color: #cfd6dc;
        box-shadow: 0 4px 14px -6px rgba(20, 40, 55, 0.08);
    }
    .field-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #4a5660;
        display: flex;
        align-items: center;
        margin-bottom: 6px;
    }
    .field-hint {
        font-size: 11px;
        line-height: 1.3;
        color: #68737c;
        margin-top: 6px;
        border-top: 1px dashed #e2e7eb;
        padding-top: 6px;
    }

    .matrix-input ::v-deep textarea {
        font-size: 13px;
        line-height: 1.35;
        font-family: Menlo, SFMono-Regular, Consolas, "Liberation Mono", monospace;
        letter-spacing: 0.2px;
    }

    /* Mobile adjustments */
    @media (max-width: 960px) {
        .matrix-head-title {
            font-size: 12.5px;
        }
        .field-block {
            padding: 12px 12px 10px;
        }
    }

    .stacked-field {
        margin-bottom: 18px;
    }
    .stacked-field:last-of-type {
        margin-bottom: 4px;
    }
    /* Falls du die bisherigen md=4 Styles noch brauchst, bleiben sie ohne Einfluss */
    @media (min-width: 961px) {
        .stacked-field {
            /* Optional: leicht breitere Anmutung */
        }
    }

    .adv-gen-icon {
        background: linear-gradient(135deg, #0d817d, rgb(20, 151, 184) 45%, #5cc6ff);
        -webkit-background-clip: text;
        color: transparent !important;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        font-size: 18px !important;
        line-height: 1;
    }

    .advanced-progress-wrapper {
        background: #f4f7f9;
        border: 1px solid #e1e6ea;
        border-radius: 8px;
        padding: 10px 14px 12px;
    }
    .progress-meta .progress-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        color: #42505a;
    }
    .advanced-done-alert {
        background: #f3fbf5 !important;
        border: 1px solid #cde8d4 !important;
        color: #275631 !important;
    }

    /* Optional Alternativen:
       mdi-auto-fix    -> stärkerer "Generate" Fokus
       mdi-shape-outline / mdi-shape             -> Struktur / Matrix
       mdi-robot-outline / mdi-robot-excited-outline -> AI Fokus
       mdi-cog-sync / mdi-cogs -> Prozess / Engine
       mdi-graph-outline -> Vernetzung / Kombination
    */

    .advanced-preview-card {
        padding: 20px 24px 0;
        border-radius: 12px;
    }

    .preview-table-wrapper {
        /* volle Breite: negative Ränder entfernen Innenabstand */
        margin: 0 -24px -24px;
    }

    .preview-table-wrapper .advanced-preview-table {
        width: 100%;
        border: none;
        border-top: 1px solid #e2e7ec;
        /* Unterseite des Cards bildet Rahmen */
    }

    .preview-table-wrapper .advanced-preview-table ::v-deep thead th {
        border-top: none;
    }

    .top-bar-inline {
        padding-left: 24px !important;
        padding-right: 24px !important;
    }

    .card-head-inline {
        padding-right: 4px;
    }
</style>
